import type { AuthUser, UserRole } from './types';

/**
 * The RBAC matrix, mirroring the backend's. It decides what the UI shows;
 * the server decides what actually happens. Keeping a copy here is what lets
 * the app hide a button rather than letting a user press it and receive a 403.
 *
 * If these two ever disagree, the server wins — by definition.
 */
export type Permission = `${string}:${string}`;

const ROLE_PERMISSIONS: Record<UserRole, readonly Permission[]> = {
  ADMIN: [
    'users:*',
    'agents:*',
    'routes:*',
    'shops:*',
    'banks:*',
    'invoices:*',
    'collections:read',
    'collections:void',
    'reports:*',
    'dashboard:read',
    'audit:read',
  ],
  FINANCE: [
    'invoices:*',
    'shops:read',
    'shops:create',
    'shops:update',
    'agents:read',
    'routes:read',
    'banks:read',
    'collections:read',
    'reports:*',
    'dashboard:read',
  ],
  AGENT: [
    'collections:create',
    'collections:read',
    'invoices:read',
    'shops:read',
    'banks:read',
    'reports:read',
  ],
};

export function permissionsFor(role: UserRole): readonly Permission[] {
  return ROLE_PERMISSIONS[role] ?? [];
}

export function can(user: AuthUser | null, permission: Permission): boolean {
  if (!user) return false;

  const granted = permissionsFor(user.role);
  if (granted.includes(permission)) return true;

  const [resource] = permission.split(':');
  return granted.includes(`${resource}:*` as Permission);
}

export function canAny(user: AuthUser | null, permissions: readonly Permission[]): boolean {
  return permissions.some((permission) => can(user, permission));
}

/** Agents see only their own rows; the server enforces it, this hides the rest. */
export function isAgent(user: AuthUser | null): boolean {
  return user?.role === 'AGENT';
}

export function isAdmin(user: AuthUser | null): boolean {
  return user?.role === 'ADMIN';
}
